import React from 'react';
import { PieChart } from '@mui/x-charts/PieChart';
import { Card, CardContent, Typography, Box } from '@mui/material';

interface StatusDistributionChartProps {
  data: { [key: string]: number };
  loading?: boolean;
}

const StatusDistributionChart: React.FC<StatusDistributionChartProps> = ({ data, loading }) => {
  if (loading || !data) {
    return (
      <Card sx={{ height: '100%' }}>
        <CardContent>
          <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography color="text.secondary">Loading status distribution...</Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  // Transform data for pie chart
  const pieData = Object.entries(data).map(([status, count], index) => ({
    id: index,
    value: count,
    label: status,
  }));

  // Define colors for different statuses
  const statusColors: { [key: string]: string } = {
    'ACTIVE': '#28a745',
    'CANCELLED': '#dc3545',
    'COMPLETED': '#007bff',
    'PENDING': '#ffc107',
    'PROCESSING': '#17a2b8',
  };

  const coloredData = pieData.map(item => ({
    ...item,
    color: statusColors[item.label] || '#6c757d',
  }));

  const total = pieData.reduce((sum, item) => sum + item.value, 0);

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Claim Status Distribution
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Total: {total.toLocaleString()} claims
        </Typography>
        <Box sx={{ width: '100%', height: 250 }}>
          <PieChart
            series={[
              {
                data: coloredData,
                highlightScope: { fade: 'global', highlight: 'item' },
                faded: { innerRadius: 30, additionalRadius: -30, color: 'gray' },
                valueFormatter: ({ value }: { value: number }) =>
                  `${value.toLocaleString()} (${((value / total) * 100).toFixed(1)}%)`,
              },
            ]}
            slotProps={{
              legend: {
                direction: 'column' as any,
                position: { vertical: 'middle', horizontal: 'end' },
              },
            }}
            margin={{ top: 20, bottom: 20, left: 20, right: 100 }}
          />
        </Box>
      </CardContent>
    </Card>
  );
};

export default StatusDistributionChart;
